import { List, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { BOOKING_URL } from '../lib/blogToc'
import { revealActive, TocList } from './BlogSidebar'

/**
 * Mobile / tablet counterpart of the desktop sidebar: a slim bar pinned to the
 * bottom with "Contents" (opens the article index as a sheet) and "Book Now".
 * It only appears while the reader is inside the article.
 */
const BlogMobileNav = ({ sections, activeId, onSelect, visible }) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const listRef = useRef(null)

  // The sheet only needs the current section at the moment it opens.
  const activeIdRef = useRef(activeId)
  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  useEffect(() => {
    if (!sheetOpen) return undefined

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setSheetOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Wait for the sheet to be laid out, then bring the current section into view.
    const frame = window.requestAnimationFrame(() =>
      revealActive(listRef.current, activeIdRef.current, 'auto')
    )

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
      window.cancelAnimationFrame(frame)
    }
  }, [sheetOpen])

  // If the bar hides (e.g. the reader reached the end), make sure the sheet is closed.
  useEffect(() => {
    if (!visible) setSheetOpen(false)
  }, [visible])

  const handleSelect = (id) => {
    setSheetOpen(false)
    // Let the sheet release the page scroll lock before scrolling.
    window.setTimeout(() => onSelect(id), 80)
  }

  return (
    <div className="hidden lg:block">
      {/* Bottom bar */}
      <div
        inert={!visible}
        className={`fixed inset-x-0 bottom-0 z-40 transition-transform duration-300 ease-out motion-reduce:transition-none ${
          visible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        <div className="border-t border-orange-200/20 bg-neutral-950/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-lg">
          <div className="mx-auto flex max-w-xl items-center gap-3">
            {sections.length > 0 && (
              <button
                type="button"
                onClick={() => setSheetOpen(true)}
                className="flex shrink-0 items-center gap-2 rounded border border-neutral-700 px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-neutral-200 transition-colors hover:border-orange-200/60 hover:text-orange-200"
              >
                <List className="h-4 w-4" />
                Contents
              </button>
            )}
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 rounded bg-orange-200 px-5 py-2.5 text-center text-xs font-semibold uppercase tracking-wider text-neutral-950 transition-colors hover:bg-orange-300"
            >
              Book Now
            </a>
          </div>
        </div>
      </div>

      {/* Article index sheet */}
      {sections.length > 0 && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="In this article"
          inert={!sheetOpen}
          className={`fixed inset-0 z-[60] ${sheetOpen ? '' : 'pointer-events-none'}`}
        >
          <button
            type="button"
            tabIndex={-1}
            aria-label="Close index"
            onClick={() => setSheetOpen(false)}
            className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 motion-reduce:transition-none ${
              sheetOpen ? 'opacity-100' : 'opacity-0'
            }`}
          />

          <div
            className={`absolute inset-x-0 bottom-0 mx-auto flex max-h-[75vh] max-w-xl flex-col rounded-t-2xl border-t border-orange-200/20 bg-neutral-950 transition-transform duration-300 ease-out motion-reduce:transition-none ${
              sheetOpen ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pb-3 pt-5">
              <span className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.25em] text-neutral-200">
                <span className="h-1.5 w-1.5 rounded-full bg-orange-200" />
                In this article
              </span>
              <button
                type="button"
                onClick={() => setSheetOpen(false)}
                aria-label="Close"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-900 text-neutral-400 transition-colors hover:text-orange-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div
              ref={listRef}
              className="toc-scroll overflow-y-auto px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-1"
            >
              <TocList
                sections={sections}
                activeId={activeId}
                onSelect={handleSelect}
                comfortable
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BlogMobileNav
